/**
 * PlatformDealflowLocked — the free-tier teaser shown in place of the full
 * platform dealflow pipeline (D3 gate). Free investors keep their inbox
 * (score decks they receive) but the curated platform pipeline is a paid
 * entitlement; this explains the value and routes to upgrade.
 *
 * `variant="screen"` is the standalone dealflow-tab body; `variant="section"`
 * is the compact card that replaces the Deal Pipeline block on the dashboard.
 */

import React from 'react';
import { View, Text } from 'react-native';
import { Lock, Compass, Sparkles } from 'lucide-react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { UpgradePrompt } from '@/components/subscription/UpgradePrompt';
import { COPY } from '@/lib/commerce/copy';

interface PlatformDealflowLockedProps {
  variant?: 'screen' | 'section';
}

const BENEFITS = [
  'Curated feed of investment-ready startups',
  'Startups actively raising, matched to your thesis',
  'Compare, shortlist and track deals in one pipeline',
];

export function PlatformDealflowLocked({ variant = 'screen' }: PlatformDealflowLockedProps) {
  const colors = useThemeColors();
  const isSection = variant === 'section';

  return (
    <Animated.View entering={FadeInUp.duration(400)} className={isSection ? '' : 'px-5 pt-2'}>
      <View
        className="rounded-3xl overflow-hidden"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
          padding: isWeb ? 24 : 18,
        }}
      >
        {/* Header */}
        <View className="flex-row items-center gap-3 mb-3">
          <View
            className="w-11 h-11 rounded-2xl items-center justify-center"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <Compass size={22} color={colors.primary[600]} />
          </View>
          <View className="flex-1">
            <View className="flex-row items-center gap-2">
              <Text
                style={{ color: colors.text.primary }}
                className="text-lg font-bold"
                numberOfLines={1}
              >
                Platform Dealflow
              </Text>
              <View
                className="flex-row items-center gap-1 px-2 py-0.5 rounded-full"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <Lock size={11} color={colors.text.tertiary} />
                <Text style={{ color: colors.text.tertiary }} className="text-xs font-medium">
                  Locked
                </Text>
              </View>
            </View>
            <Text style={{ color: colors.text.secondary }} className="text-sm mt-0.5">
              {COPY.dealflowLockedSubtitle}
            </Text>
          </View>
        </View>

        {/* Benefits */}
        <View className="gap-2.5 mt-1 mb-4">
          {BENEFITS.map((benefit, i) => (
            <View key={`dealflow-benefit-${i}`} className="flex-row items-center gap-2.5">
              <View
                className="w-5 h-5 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <Sparkles size={12} color={colors.primary[600]} />
              </View>
              <Text
                style={{ color: colors.text.secondary }}
                className="text-sm flex-1"
                numberOfLines={2}
              >
                {benefit}
              </Text>
            </View>
          ))}
        </View>

        {/* Upgrade prompt (next tier + unlocked features + price) */}
        <UpgradePrompt variant="card" headline="Unlock Platform Dealflow" lockedTitle="Platform dealflow" />

        {!isSection && (
          <Text style={{ color: colors.text.tertiary }} className="text-xs text-center mt-4">
            You can still analyze decks founders send you from your inbox.
          </Text>
        )}
      </View>
    </Animated.View>
  );
}
